import React, { ReactElement, useState, useEffect, useContext, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  TableRow,
  TableCell,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  Feedback as FeedbackIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  AccessTime as AccessTimeIcon,
  Visibility,
  Edit,
  Refresh
} from '@mui/icons-material';
import { UserContext } from '../../contexts/UserContext';
import { HttpClient } from '../../api/HttpClient';
import { UserFeedback, FeedbackStatus, PaginatedFeedbackResponse } from '../../api/models/UserFeedback';
import { AdminLayout } from './shared/AdminLayout';
import { AdminTable, AdminTableColumn } from './shared/AdminTable';
import { AdminPagination } from './shared/AdminPagination';
import { AdminEmptyState } from './shared/AdminEmptyState';

export function AdminFeedback(): ReactElement {
  const { authToken } = useContext(UserContext);
  const [feedback, setFeedback] = useState<UserFeedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedFeedback, setSelectedFeedback] = useState<UserFeedback | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showStatusUpdate, setShowStatusUpdate] = useState(false);
  const [newStatus, setNewStatus] = useState<FeedbackStatus>(FeedbackStatus.Pending);
  const [adminNotes, setAdminNotes] = useState('');
  const [filters, setFilters] = useState({
    feedbackType: '',
    status: '',
    page: 1,
    pageSize: 20
  });
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortBy, setSortBy] = useState<string>('submittedAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const httpClient = useMemo(() => {
    return authToken ? new HttpClient(authToken) : null;
  }, [authToken]);

  const loadFeedback = useCallback(async () => {
    if (!httpClient) return;
    
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.feedbackType) params.append('feedbackType', filters.feedbackType);
      if (filters.status) params.append('status', filters.status);
      params.append('page', filters.page.toString());
      params.append('pageSize', filters.pageSize.toString());
      params.append('sortBy', sortBy);
      params.append('sortOrder', sortOrder);
      
      const queryString = params.toString();
      const response = await httpClient.get<PaginatedFeedbackResponse>(`/userfeedback/admin?${queryString}`);
      
      setFeedback(response.feedback);
      setTotalCount(response.totalCount);
      setTotalPages(response.totalPages);
      setCurrentPage(response.page);
      setError(null);
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'status' in err && (err as { status: number }).status === 403) {
        setError('Access denied. You must be an administrator to view this page.');
      } else {
        setError('Failed to fetch feedback');
      }
      console.error('Error fetching feedback:', err);
    } finally {
      setLoading(false);
    }
  }, [httpClient, filters, sortBy, sortOrder]);

  useEffect(() => {
    loadFeedback();
  }, [loadFeedback]);

  const updateFeedbackStatus = useCallback(async (feedbackId: string, status: FeedbackStatus, notes: string) => {
    if (!httpClient) return;
    
    try {
      await httpClient.put(`/userfeedback/${feedbackId}/status`, { status, adminNotes: notes });
      await loadFeedback();
      setShowStatusUpdate(false);
      setShowDetails(false);
    } catch (err) {
      console.error('Failed to update feedback status:', err);
    }
  }, [httpClient, loadFeedback]);

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  const getSortIcon = (field: string) => {
    if (sortBy !== field) return '↕️';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const getStatusColor = (status: FeedbackStatus) => {
    switch (status) {
      case FeedbackStatus.Pending:
        return 'warning';
      case FeedbackStatus.Reviewed:
        return 'info';
      case FeedbackStatus.Actioned:
        return 'success';
      case FeedbackStatus.Closed:
        return 'default';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const handleRefresh = () => {
    loadFeedback();
  };

  const columns: AdminTableColumn[] = [
    {
      key: 'userEmail',
      label: 'User Email',
      width: '25%',
      minWidth: 200,
      sortable: true,
      icon: <PersonIcon fontSize="small" color="action" />,
      sortIcon: getSortIcon('userEmail'),
      onClick: () => handleSort('userEmail')
    },
    {
      key: 'feedbackType',
      label: 'Type',
      width: '15%',
      minWidth: 120,
      sortable: true,
      icon: <FeedbackIcon fontSize="small" color="action" />,
      sortIcon: getSortIcon('feedbackType'),
      onClick: () => handleSort('feedbackType')
    },
    {
      key: 'status',
      label: 'Status',
      width: '12%',
      minWidth: 100,
      sortable: true,
      icon: <ScheduleIcon fontSize="small" color="action" />,
      sortIcon: getSortIcon('status'),
      onClick: () => handleSort('status')
    },
    {
      key: 'submittedAt',
      label: 'Submitted',
      width: '15%',
      minWidth: 120,
      sortable: true,
      icon: <AccessTimeIcon fontSize="small" color="action" />,
      sortIcon: getSortIcon('submittedAt'),
      onClick: () => handleSort('submittedAt')
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '10%',
      minWidth: 100
    }
  ];

  const renderRow = (item: UserFeedback) => (
    <TableRow 
      key={item.id}
      sx={{ 
        '&:hover': { 
          bgcolor: 'action.hover',
          transform: 'translateY(-1px)',
          boxShadow: (theme) => theme.palette.mode === 'dark' ? 3 : 2,
          '& .MuiTableCell-root': {
            borderBottom: '1px solid',
            borderBottomColor: 'divider'
          }
        },
        transition: 'all 0.2s ease-in-out',
        cursor: 'pointer',
        '&:nth-of-type(even)': {
          bgcolor: 'action.selected'
        },
        '& .MuiTableCell-root': {
          borderBottom: '1px solid',
          borderBottomColor: 'divider',
          color: 'text.primary'
        }
      }}
      onClick={() => {
        setSelectedFeedback(item);
        setShowDetails(true);
      }}
    >
      <TableCell sx={{ py: 2, width: '25%', minWidth: 200 }}>
        <Typography 
          variant="body2" 
          sx={{ 
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          title={item.userEmail}
        >
          {item.userEmail}
        </Typography>
      </TableCell>
      <TableCell sx={{ py: 2, width: '15%', minWidth: 120 }}>
        <Chip 
          label={item.feedbackType.replace('_', ' ').toUpperCase()} 
          color="primary"
          size="small"
          sx={{ 
            fontWeight: 500,
            textTransform: 'capitalize',
            '& .MuiChip-label': {
              color: 'inherit'
            }
          }}
        />
      </TableCell>
      <TableCell sx={{ py: 2, width: '12%', minWidth: 100 }}>
        <Chip 
          label={FeedbackStatus[item.status]} 
          color={getStatusColor(item.status)}
          size="small"
          sx={{ 
            fontWeight: 500,
            textTransform: 'capitalize',
            '& .MuiChip-label': {
              color: 'inherit'
            }
          }}
        />
      </TableCell>
      <TableCell sx={{ py: 2, width: '15%', minWidth: 120 }}>
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          title={formatDate(item.submittedAt)}
        >
          {formatDate(item.submittedAt)}
        </Typography>
      </TableCell>
      <TableCell sx={{ py: 2, width: '10%', minWidth: 100 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="View Details">
            <IconButton 
              size="small" 
              onClick={(e) => {
                e.stopPropagation();
                setSelectedFeedback(item);
                setShowDetails(true);
              }}
            >
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Update Status">
            <IconButton 
              size="small" 
              onClick={(e) => {
                e.stopPropagation();
                setSelectedFeedback(item);
                setNewStatus(item.status);
                setAdminNotes(item.adminNotes || '');
                setShowStatusUpdate(true);
              }}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );

  const filtersComponent = (
    <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
      <FormControl sx={{ minWidth: 200 }}>
        <InputLabel>Feedback Type</InputLabel>
        <Select
          value={filters.feedbackType}
          label="Feedback Type"
          onChange={(e) => setFilters(prev => ({ ...prev, feedbackType: e.target.value, page: 1 }))}
        >
          <MenuItem value="">All Types</MenuItem>
          <MenuItem value="trial_extension">Trial Extension</MenuItem>
          <MenuItem value="feature_request">Feature Request</MenuItem>
          <MenuItem value="bug_report">Bug Report</MenuItem>
          <MenuItem value="general_feedback">General Feedback</MenuItem>
        </Select>
      </FormControl>
      <FormControl sx={{ minWidth: 200 }}>
        <InputLabel>Status</InputLabel>
        <Select
          value={filters.status}
          label="Status"
          onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value, page: 1 }))}
        >
          <MenuItem value="">All Statuses</MenuItem>
          <MenuItem value={FeedbackStatus.Pending}>Pending</MenuItem>
          <MenuItem value={FeedbackStatus.Reviewed}>Reviewed</MenuItem>
          <MenuItem value={FeedbackStatus.Actioned}>Actioned</MenuItem>
          <MenuItem value={FeedbackStatus.Closed}>Closed</MenuItem>
        </Select>
      </FormControl>
    </Stack>
  );

  const resultsSummary = (
    <Box sx={{ 
      mb: 2, 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 2
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Showing {feedback.length} of {totalCount} feedback entries
          {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
        </Typography>
      </Box>
      
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <FormControl size="small" sx={{ minWidth: 80 }}>
          <InputLabel>Per Page</InputLabel>
          <Select
            value={pageSize}
            label="Per Page"
            onChange={(e) => {
              const newPageSize = Number(e.target.value);
              setPageSize(newPageSize);
              setCurrentPage(1);
              setFilters(prev => ({ ...prev, pageSize: newPageSize, page: 1 }));
            }}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={handleRefresh}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>
    </Box>
  );

  const paginationComponent = (
    <AdminPagination
      currentPage={currentPage}
      totalPages={totalPages}
      totalCount={totalCount}
      pageSize={pageSize}
      loading={loading}
      onChange={(page) => {
        setCurrentPage(page);
        setFilters(prev => ({ ...prev, page }));
      }}
    />
  );

  const emptyStateComponent = (
    <AdminEmptyState
      icon={<FeedbackIcon />}
      title="No feedback found"
      description="Try adjusting your filters to see more results"
    />
  );

  return (
    <AdminLayout
      title="User Feedback"
      description="Review and manage user feedback, trial extension requests, and feature suggestions"
      filters={filtersComponent}
      resultsSummary={resultsSummary}
      table={
        <AdminTable
          columns={columns}
          data={feedback}
          loading={loading}
          renderRow={renderRow}
          emptyState={emptyStateComponent}
        />
      }
      pagination={paginationComponent}
      error={error}
    >
      {/* Feedback Details Dialog */}
      <Dialog open={showDetails} onClose={() => setShowDetails(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h5" component="div">
            Feedback Details
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedFeedback && (
            <>
              <Typography variant="body2" gutterBottom>
                <strong>User:</strong> {selectedFeedback.userEmail}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Type:</strong> {selectedFeedback.feedbackType}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Status:</strong> {FeedbackStatus[selectedFeedback.status]}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Submitted:</strong> {formatDate(selectedFeedback.submittedAt)}
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Responses</Typography>
              {(() => {
                const responses = selectedFeedback.responses;
                const mainQuestions = [
                  { key: 'preventing_upgrade', label: 'What\'s preventing you from upgrading?', otherKey: 'preventing_upgrade_other' },
                  { key: 'desired_features', label: 'What features would make you more likely to upgrade?', otherKey: 'desired_features_other' },
                  { key: 'budget_concerns', label: 'Budget concerns', otherKey: 'budget_concerns_other' },
                  { key: 'timeline', label: 'When would you consider upgrading?' }
                ];

                return mainQuestions.map((question) => {
                  const mainValue = responses[question.key];
                  const otherValue = question.otherKey ? responses[question.otherKey] : null;
                  
                  if (!mainValue) return null;

                  return (
                    <Box key={question.key} sx={{ mb: 3 }}>
                      <>
                        <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 1, color: 'text.primary' }}>
                          {question.label}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: otherValue ? 1 : 0, color: 'text.secondary' }}>
                          {String(mainValue)}
                        </Typography>
                        {otherValue && (
                          <Box sx={{ mt: 1, pl: 2, borderLeft: '3px solid', borderLeftColor: 'primary.main' }}>
                            <Typography variant="body2" fontWeight="500" sx={{ mb: 0.5, color: 'text.primary' }}>
                              Additional details:
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                              {String(otherValue)}
                            </Typography>
                          </Box>
                        )}
                      </>
                    </Box>
                  );
                });
              })()}

              {selectedFeedback.additionalComments && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Additional Comments</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selectedFeedback.additionalComments}
                  </Typography>
                </>
              )}

              {selectedFeedback.adminNotes && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Admin Notes</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selectedFeedback.adminNotes}
                  </Typography>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDetails(false)}>Close</Button>
          <Button 
            variant="contained" 
            onClick={() => {
              setShowDetails(false);
              setNewStatus(selectedFeedback?.status || FeedbackStatus.Pending);
              setAdminNotes(selectedFeedback?.adminNotes || '');
              setShowStatusUpdate(true);
            }}
          >
            Update Status
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog open={showStatusUpdate} onClose={() => setShowStatusUpdate(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h5" component="div">
            Update Feedback Status
          </Typography>
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={newStatus}
              label="Status"
              onChange={(e) => setNewStatus(e.target.value as FeedbackStatus)}
            >
              <MenuItem value={FeedbackStatus.Pending}>Pending</MenuItem>
              <MenuItem value={FeedbackStatus.Reviewed}>Reviewed</MenuItem>
              <MenuItem value={FeedbackStatus.Actioned}>Actioned</MenuItem>
              <MenuItem value={FeedbackStatus.Closed}>Closed</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Admin Notes"
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowStatusUpdate(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={() => {
              if (selectedFeedback) {
                updateFeedbackStatus(selectedFeedback.id, newStatus, adminNotes);
              }
            }}
            startIcon={loading ? <CircularProgress size={20} /> : null}
            disabled={loading}
          >
            Update Status
          </Button>
        </DialogActions>
      </Dialog>
    </AdminLayout>
  );
}